/**
 * Resolves an image URL to ensure it works with the current backend
 * Handles:
 * - Relative paths (/api/images/xxx) - prepends current backend URL
 * - Old preview URLs - extracts the image ID and rebuilds with current backend
 * - External URLs (https://...) - returns as-is if valid, or fixes if from old preview domains
 * - Blob URLs - returns placeholder (these are temporary and shouldn't be stored)
 */
export const resolveImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Handle blob URLs (temporary browser URLs - shouldn't be stored)
  if (imageUrl.startsWith('blob:')) {
    console.warn('Blob URL detected - this should not be stored in database:', imageUrl);
    return null;
  }
  
  // Handle relative paths (correct format)
  if (imageUrl.startsWith('/api/images/')) {
    return `${backendUrl}${imageUrl}`;
  }
  
  // Handle full URLs that contain /api/images/ (from old preview domains)
  if (imageUrl.includes('/api/images/')) {
    // Extract the image ID from the URL
    const match = imageUrl.match(/\/api\/images\/([a-f0-9-]+)/i);
    if (match) {
      const imageId = match[1];
      return `${backendUrl}/api/images/${imageId}`;
    }
  }
  
  // Handle external URLs (e.g., customer-assets.emergentagent.com) - return as-is
  if (imageUrl.startsWith('https://customer-assets.') || 
      imageUrl.startsWith('https://images.unsplash.com')) {
    return imageUrl;
  }
  
  // For any other URL, try to use it as-is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If nothing matches, prepend backend URL as fallback
  return `${backendUrl}${imageUrl}`;
};

/**
 * Resolves an array of image URLs
 */
export const resolveImageUrls = (imageUrls) => {
  if (!Array.isArray(imageUrls)) return [];
  return imageUrls.map(resolveImageUrl).filter(Boolean);
};

/**
 * Extracts just the relative path from an image URL (for storage)
 * This ensures we don't store full domain URLs that break when domain changes
 */
export const extractRelativePath = (imageUrl) => {
  if (!imageUrl) return null;
  
  // If it's already a relative path, return as-is
  if (imageUrl.startsWith('/api/images/')) {
    return imageUrl;
  }
  
  // Extract from full URL
  if (imageUrl.includes('/api/images/')) {
    const match = imageUrl.match(/(\/api\/images\/[a-f0-9-]+)/i);
    if (match) {
      return match[1];
    }
  }
  
  // For external URLs (like customer-assets), return as-is
  if (imageUrl.startsWith('https://customer-assets.') || 
      imageUrl.startsWith('https://images.unsplash.com')) {
    return imageUrl;
  }
  
  return imageUrl;
};
